import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { PageHeader } from './PageHeader';
import { getEmotion, getDistortion, type Practice } from '../data/cbtContent';
import './JournalEntryViewPage.css';

interface JournalEntry {
  id: string;
  emotion: string;
  intensity: number;
  body_feelings: string | null;
  situation: string;
  automatic_thought: string;
  distortions: string[];
  alternative_thought: string | null;
  new_intensity: number | null;
  created_at: string;
}

export const JournalEntryViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    apiClient.getJournalEntry(id)
      .then(async (res) => {
        const e: JournalEntry = res.data;
        setEntry(e);
        try {
          const p = await apiClient.getPracticesByEmotion(e.emotion);
          setPractices(p.data ?? []);
        } catch {}
      })
      .catch((e) => console.error('Failed to load entry', e))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="cbt-loading">Загружаем…</div>;
  if (!entry) return <div className="cbt-loading">Запись не найдена</div>;

  const emotion = getEmotion(entry.emotion);
  const dateLabel = new Date(entry.created_at).toLocaleString('ru-RU', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="cbt-view-page">
      <PageHeader title="Запись дневника" onBack={() => navigate('/journal')} />

      <div className="cbt-view-emotion" style={{ background: `${emotion?.color}20`, borderColor: emotion?.color }}>
        <div className="cbt-view-emotion-row">
          <span className="cbt-view-big-emoji">{emotion?.emoji}</span>
          <div>
            <div className="cbt-view-emotion-name" style={{ color: emotion?.color }}>{emotion?.label}</div>
            <div className="cbt-view-date">{dateLabel}</div>
          </div>
        </div>
        <div className="cbt-view-intensity">
          <div className="cbt-view-intensity-label">Интенсивность: {entry.intensity} / 10</div>
          <div className="cbt-view-bar-track">
            <div className="cbt-view-bar-fill" style={{ width: `${entry.intensity * 10}%`, background: emotion?.color }} />
          </div>
        </div>
      </div>

      {entry.body_feelings && (
        <section className="cbt-view-section">
          <h3>Ощущения в теле</h3>
          <p>{entry.body_feelings}</p>
        </section>
      )}

      <section className="cbt-view-section">
        <h3>Ситуация</h3>
        <p>{entry.situation}</p>
      </section>

      <section className="cbt-view-section">
        <h3>Автоматическая мысль</h3>
        <div className="cbt-view-quote">«{entry.automatic_thought}»</div>
      </section>

      {entry.distortions?.length > 0 && (
        <section className="cbt-view-section">
          <h3>Выявленные искажения</h3>
          <div className="cbt-view-distortions">
            {entry.distortions.map((d) => {
              const dist = getDistortion(d);
              if (!dist) return null;
              return (
                <div className="cbt-view-distortion-tile" key={d}>
                  <span className="cbt-view-distortion-emoji">{dist.emoji}</span>
                  <span className="cbt-view-distortion-label">{dist.label}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {entry.alternative_thought && (
        <section className="cbt-view-section">
          <h3>Альтернативная мысль</h3>
          <div className="cbt-view-quote cbt-view-quote-positive">«{entry.alternative_thought}»</div>

          {entry.new_intensity !== null && (
            <div className="cbt-view-compare">
              <div className="cbt-view-compare-side">
                <div className="cbt-view-compare-label">До</div>
                <div className="cbt-view-compare-value">{entry.intensity}</div>
                <div className="cbt-view-bar-track">
                  <div className="cbt-view-bar-fill" style={{ width: `${entry.intensity * 10}%`, background: emotion?.color }} />
                </div>
              </div>
              <div className="cbt-view-compare-arrow">→</div>
              <div className="cbt-view-compare-side">
                <div className="cbt-view-compare-label">После</div>
                <div className="cbt-view-compare-value">{entry.new_intensity}</div>
                <div className="cbt-view-bar-track">
                  <div className="cbt-view-bar-fill cbt-positive" style={{ width: `${entry.new_intensity * 10}%` }} />
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {practices.length > 0 && (
        <section className="cbt-view-section">
          <h3>Рекомендуемые практики</h3>
          <div className="cbt-view-practices">
            {practices.map((p) => (
              <div className="cbt-view-practice" key={p.slug}>
                <div className="cbt-view-practice-title">{p.title}</div>
                <div className="cbt-view-practice-desc">{p.description}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <button className="cbt-view-cta" onClick={() => navigate('/experts')}>
        Обсудить с экспертом →
      </button>
    </div>
  );
};
